import axios from 'axios';

/**
 * Interface representing a Bet Configuration
 */
export interface BetConfig {
  id: number;
  round: string;
  matchday?: number | null;
  type: string;
  name: string;
  description: string;
  general: boolean;
  points: number;
  view_schema?: {
    data: 'teams' | 'ranked_teams' | 'players' | 'groups' | 'number' | 'match';
    size: number;
  };
  correct_answer?: Record<string, any> | null;
}

/**
 * Interface for bet config form data
 */
export interface BetConfigFormData {
  round: string;
  matchday?: number | null;
  name: string;
  description: string;
  general: boolean;
  points: number;
  view_schema?: {
    data: 'teams' | 'ranked_teams' | 'players' | 'groups' | 'number' | 'match';
    size: number;
  };
}

/**
 * Fetches all general bet configurations from the API
 * @returns Array of bet configurations
 */
export const getGeneralBetConfigs = async (): Promise<BetConfig[]> => {
  try {
    const response = await axios.get<BetConfig[]>('/api/bet-configs/general');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching bet configurations:', error);
    return [];
  }
};

/**
 * Fetches all bet configurations from the API
 * @returns Array of bet configurations
 */
export const getBetConfigs = async (): Promise<BetConfig[]> => {
  try {
    const response = await axios.get<BetConfig[]>('/api/bet-configs');
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching bet configurations:', error);
    return [];
  }
};

/**
 * Fetches a specific bet configuration by ID
 * @param id - Bet configuration ID
 * @returns Bet configuration object
 */
export const getBetConfig = async (id: number): Promise<BetConfig> => {
  try {
    const response = await axios.get<BetConfig>(`/api/bet-configs/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching bet configuration ${id}:`, error);
    throw error;
  }
};

/**
 * Updates an existing bet configuration
 * @param id - Bet configuration ID
 * @param configData - Raw bet configuration data from form
 * @returns Updated bet configuration
 */
export const updateBetConfig = async (id: number, configData: BetConfigFormData): Promise<BetConfig> => {
  try {
    const response = await axios.put<BetConfig>(`/api/bet-configs/${id}`, configData);
    return response.data;
  } catch (error) {
    console.error(`Error updating bet configuration ${id}:`, error);
    throw error;
  }
};

/**
 * Set the correct answer for a general bet config and recalculate all user bets.
 * @param id - Bet configuration ID
 * @param correctAnswer - The correct answer object (same shape as a user prediction)
 */
export const setCorrectAnswer = async (id: number, correctAnswer: Record<string, any>): Promise<BetConfig> => {
  const response = await axios.put<BetConfig>(`/api/bet-configs/${id}/correct-answer`, { correct_answer: correctAnswer });
  return response.data;
};